import React, { createContext, useState, useEffect, useContext } from 'react';
import { Routes, Route } from 'react-router-dom';

import RequireAuth from 'helpers/components/require-auth';
import OktaLogin from 'containers/login/okta-widget/okta-login';
import MaintenancePage from 'containers/maintenance-page';
import AuthenticatedApp from 'containers/authenticated-app';
import EnrollAccount from 'containers/enroll/enroll-account';
import ResetPassword from 'containers/reset-password';
import RequestPassword from 'containers/request-password';
import ToastContainer from 'components/partials/toast/toast-container/toast-container';

import type { GetFeatures200 } from 'api/generated/schemas/getFeatures200';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

import { useRemoteFeatureFlags } from 'hooks/use-remote-feature-flags';

export type FeatureFlags = {
  articles: boolean;
  webinars: boolean;
  downloads: boolean;
  guides: boolean;
  courses: boolean;
  releaseNotes: boolean;
  'tab-goals': boolean;
  'goals-monitoring': boolean;
  'tab-insights': boolean;
  'tab-fao': boolean;
  'ai-assistant': boolean;
  'ai-guided-prompt': boolean;
  'ai-bar-chart': boolean;
};

const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  articles: false,
  webinars: true,
  downloads: true,
  guides: true,
  courses: true,
  releaseNotes: true,
  'tab-goals': false,
  'goals-monitoring': false,
  'tab-insights': false,
  'tab-fao': false,
  'ai-assistant': false,
  'ai-guided-prompt': false,
  'ai-bar-chart': false,
};

export type FeatureFlagsContextValue = {
  featureFlags: FeatureFlags;
};

export const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  featureFlags: DEFAULT_FEATURE_FLAGS,
});

export const useFeatureFlags = (): FeatureFlagsContextValue =>
  useContext(FeatureFlagsContext);

type FeatureFlagsProviderProps = {
  children: React.ReactNode;
};

const isFeatureFlagsPayload = (v: unknown): v is GetFeatures200 =>
  typeof v === 'object' && v !== null;

const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({
  children,
}) => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(
    DEFAULT_FEATURE_FLAGS
  );
  // TODO: Wave 8 — type useCurrentUser hook properly so .data?.oktaUserId
  // doesn't require any-cast workarounds in consumers.
  const user = useCurrentUser();
  const featureFlagsQuery = useRemoteFeatureFlags(user?.data?.oktaUserId);

  useEffect(() => {
    const raw = featureFlagsQuery.data;

    if (!isFeatureFlagsPayload(raw)) {
      return;
    }

    const data = raw;

    setFeatureFlags({
      ...DEFAULT_FEATURE_FLAGS,
      articles: data['cms-articles'] || DEFAULT_FEATURE_FLAGS.articles,
      'tab-goals': data['tab-goals'] || DEFAULT_FEATURE_FLAGS['tab-goals'],
      'goals-monitoring':
        data['goals-monitoring'] || DEFAULT_FEATURE_FLAGS['goals-monitoring'],
      'tab-insights': data['tab-insights'] || DEFAULT_FEATURE_FLAGS['tab-insights'],
      'tab-fao': data['tab-fao'] || DEFAULT_FEATURE_FLAGS['tab-fao'],
      'ai-assistant': data['ai-assistant'] || DEFAULT_FEATURE_FLAGS['ai-assistant'],
      'ai-guided-prompt':
        data['ai-guided-prompt'] || DEFAULT_FEATURE_FLAGS['ai-guided-prompt'],
      'ai-bar-chart':
        data['ai-bar-chart'] || DEFAULT_FEATURE_FLAGS['ai-bar-chart'],
    });
  }, [featureFlagsQuery.data]);

  useEffect(() => {
    if (!featureFlagsQuery.isError) {
      return;
    }

    console.error('Failed to fetch feature flags:', featureFlagsQuery.error);
  }, [featureFlagsQuery.isError, featureFlagsQuery.error]);

  return (
    <FeatureFlagsContext.Provider value={{ featureFlags }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

const AppRoutes: React.FC = () => (
  <Routes>
    <Route path="/login/*" element={<OktaLogin />} />
    <Route path="/implicit" element={<OktaLogin />} />
    <Route path="/implicit/callback" element={<OktaLogin />} />
    <Route
      path="/account/:oktaUserId/:activationKey"
      element={<EnrollAccount />}
    />
    <Route path="/account/reset" element={<ResetPassword />} />
    <Route
      path="/account/request-password-reset"
      element={<RequestPassword />}
    />

    <Route
      path="/*"
      element={
        <RequireAuth>
          <MaintenancePage>
            <AuthenticatedApp />
          </MaintenancePage>
        </RequireAuth>
      }
    />
  </Routes>
);

const App: React.FC = () => (
  <FeatureFlagsProvider>
    <ToastContainer />
    <AppRoutes />
    <div id="menu-portal"></div>
    <div id="page-overlay"></div>
    <div id="modal-overlay"></div>
  </FeatureFlagsProvider>
);

export default App;
